import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Grid, Typography } from '@mui/material';
import { RedTheme } from 'common/global';
import PropTypes from 'prop-types';
function GenericDataGridTable({
  gridHeight,
  gridWidth,
  pageSize,
  pageSizeOptions,
  gridRowHeight,
  gridTitle,
  gridRows,
  gridColumns = [],
  sortingMode,
  paginationMode,
  disableColumnMenuProp = false,
  hideSelectedRowCount = false,
  sortingDefaultColumn = '',
  sortingDefaultColumnMode = 'asc',
}) {
  let redtheme = RedTheme;
  const [noTrainingsAssigned, setNoTrainingsAssigned] = useState(false);
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    page: 1,
    pageSize: 5,
    total: 0,
  });

  const noDataInGridStyles = {
    border: 'none',
    minHeight: '480px',
    '& .MuiDataGrid-columnHeaders': {
      fontSize: 16,
      fontFamily: '"J&J CircularTT Black Web"',
    },
  };

  const gridStyles = {
    '& .MuiDataGrid-cell': {
      fontSize: '14px',
      fontWeight: 450,
      fontFamily: '"J&J CircularTT Book Web"',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: 'rgba(0, 0, 153, 0.03)',
    },
    '& .MuiDataGrid-row:nth-of-type(odd):hover': {
      backgroundColor: redtheme.palette.dataGrid.very_light_grey,
    },
    '& .MuiDataGrid-columnHeaders': {
      fontSize: 16,
      fontFamily: '"J&J CircularTT Black Web"',
    },
    '& .MuiDataGrid-row.Mui-selected': { backgroundColor: redtheme.palette.dataGrid.white },
    '& .MuiDataGrid-row.Mui-selected:hover': {
      backgroundColor: redtheme.palette.dataGrid.white,
    },
    '& .MuiDataGrid-row.Mui-selected:nth-of-type(odd)': {
      backgroundColor: 'rgba(0, 0, 153, 0.03)',
    },
    '& .MuiDataGrid-row.Mui-selected:nth-of-type(odd):hover': {
      backgroundColor: redtheme.palette.dataGrid.very_light_grey,
    },
    '& .MuiDataGrid-rowCount': {
      fontWeight: 'bold',
    },
    '& .MuiTablePagination-selectLabel': {
      fontFamily: '"J&J CircularTT Black Web"',
      fontSize: '16px',
      fontWeight: '700',
      lineHeight: '18px',
      fontStyle: 'normal',
      color: redtheme.palette.dataGrid.fontGrey,
    },
    '& .MuiTablePagination-displayedRows': {
      fontFamily: '"J&J CircularTT Black Web"',
      fontSize: '16px',
      fontWeight: '700',
      lineHeight: '18px',
      fontStyle: 'normal',
      color: redtheme.palette.dataGrid.fontGrey,
    },
    '& .MuiTablePagination-select': {
      fontFamily: '"J&J CircularTT Black Web"',
      fontSize: '16px',
      fontWeight: '700',
      lineHeight: '26px',
      fontStyle: 'normal',
      color: redtheme.palette.dataGrid.fontGrey,
    },
    '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
      outline: 'none !important',
    },
    '&.MuiDataGrid-root .MuiDataGrid-cellContent': {
      pointerEvents: 'none',
      fontWeight: '400',
      fontSize: '1rem',
    },
    '& .MuiDataGrid-cell--withRenderer.MuiDataGrid-cell.MuiDataGrid-cell--textLeft.MuiDataGrid-withBorderColor':
      {
        minHeight: `${gridRowHeight}px !important`,
        maxHeight: `${gridRowHeight}px !important`,
      },
    '& .MuiDataGrid-row': {
      minHeight: `${gridRowHeight}px !important`,
      maxHeight: `${gridRowHeight}px !important`,
    },
    ...(hideSelectedRowCount && {
      '& .MuiDataGrid-selectedRowCount': {
        display: 'none',
      },
    }),
  };

  const customNoDataOverlay = () => {
    let overlayMssg = 'No Trainings assigned to you at the moment';

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0px',
          background: redtheme.palette.box.very_light_grey,
          fontWeight: '900',
          fontSize: '26px',
          color: redtheme.palette.box.dark_grayish_blue,
          width: '100%',
          height: '100%',
        }}
      >
        {overlayMssg}
      </Box>
    );
  };

  useEffect(() => {
    setNoTrainingsAssigned(false);
    setPageState({ ...pageState, isLoading: false });
  }, []);

  return (
    <Grid
      container
      direction='column'
      spacing={1}
      sx={{ background: redtheme.palette.background.default, width: '100%', marginTop: '0px' }} // Adjust padding
      data-testid='Generic-Grid-Table'
    >
      {gridTitle && (
        <Grid item>
          <Typography
            component={gridTitle.setting.componentElement}
            variant={gridTitle.setting.variant}
            sx={{
              fontSize: gridTitle.setting.fontSize,
              fontWeight: gridTitle.setting.fontWeight,
              marginRight: gridTitle.setting.marginRight,
              fontFamily: gridTitle.setting.fontFamily,
              padding: gridTitle.setting.padding,
            }}
          >
            {gridTitle.name || ''}
          </Typography>
        </Grid>
      )}
      <Grid item xs sx={{ maxWidth: '100% !important' }}>
        <Box
          sx={{
            height: { xs: '300px', sm: `${gridHeight}px` }, // Adjust height based on screen size
            width: { xs: '100%', sm: `${gridWidth}px` },
            maxWidth: '100% !important',
          }}
        >
          <DataGrid
            disableVirtualization={process.env.NODE_ENV === 'test'}
            rows={gridRows}
            columns={gridColumns}
            getRowId={(row) => row.user_id}
            loading={pageState.isLoading}
            sortingMode={sortingMode}
            paginationMode={paginationMode}
            rowCount={gridRows.length}
            initialState={
              sortingDefaultColumn
                ? {
                    pagination: { paginationModel: { pageSize: pageSize } },
                    sorting: {
                      sortModel: [{ field: sortingDefaultColumn, sort: sortingDefaultColumnMode }],
                    },
                  }
                : { paginationModel: { pageSize: pageSize } }
            }
            pageSizeOptions={pageSizeOptions}
            disableColumnMenu={disableColumnMenuProp}
            slots={{
              noRowsOverlay: customNoDataOverlay,
            }}
            onPaginationModelChange={(newPage) => {
              setPageState((old) => ({
                ...old,
                page: newPage.page + 1,
                pageSize: newPage.pageSize,
              }));
            }}
            onPageSizeChange={(newPageSize) =>
              setPageState((old) => ({ ...old, pageSize: newPageSize }))
            }
            sx={noTrainingsAssigned ? noDataInGridStyles : gridStyles}
          />
        </Box>
      </Grid>
    </Grid>
  );
}

export default GenericDataGridTable;

GenericDataGridTable.propTypes = {
  gridHeight: PropTypes.number,
  gridWidth: PropTypes.number,
  columnNames: PropTypes.array,
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.array,
  gridRowHeight: PropTypes.number,
  gridTitle: PropTypes.object,
  gridRows: PropTypes.array,
  gridColumns: PropTypes.array,
  sortingMode: PropTypes.string,
  paginationMode: PropTypes.string,
};
